import { useState } from 'react';
import styled from 'styled-components';

import {
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { Save, SaveAlt, Edit, FileCopy, Delete, CloudDownloadOutlined } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { saveWorkflow } from '@requests/flow';
import { TemplateType } from '@requests/templates';
import { useTheme } from '../../../styles/theme';
import { AppDispatch, State, flowActions, tabActions, FlowModals, showTelnyxFeatures } from '@store/index';
import { Button } from '@common/Button';
import { Resource } from '@store/types';
import { useTranslate } from '@common/hooks/useTranslate';
import { usePermissions } from '@common/hooks/usePermissions';
import { useWorkflow } from '@common/hooks/useWorkflow';
import { TriggerCommandIdMap } from '@components/Modals/Templates/SaveAsTemplate';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 30px;
  gap: 4px;

  & .MuiIconButton-root {
    padding: 3px;
  }

  svg {
    font-size: 18px;
  }
`;

const StyledMenu = styled(Menu)`
  & .MuiPaper-root {
    border-radius: 6px;
    margin-top: ${({ theme }) => theme.spacing(1)};
    min-width: 300px;
    color: rgb(55, 65, 81);
    box-shadow: rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px,
      rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;

    & .MuiMenu-list {
      padding: 4px 0;
    }

    & .MuiMenuItem-root {
      & .MuiSvgIcon-root {
        font-size: 18px;
        color: ${({ theme }) => theme.text.subtext};
        margin-right: ${({ theme }) => theme.spacing(1.5)};
      }
    }
  }
`;

const ExecutionIcon = styled.div<{ active?: boolean }>`
  height: 11px;
  width: 11px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.text.standard};
  background-color: ${({ active, theme }) => (active ? theme.text.standard : 'transparent')};
  margin-right: 13px;
  margin-left: 2px;
`;

const AttributeIcon = styled.div<{ color?: string; active?: boolean }>`
  height: 11px;
  width: 11px;
  border-radius: 1rem;
  border: 1px solid ${({ color }) => color};
  background-color: ${({ active, color }) => (active ? color : 'transparent')};
  margin-right: 13px;
  margin-left: 2px;
`;

// const getControlIcon = () => {
//   const platform = navigator.userAgent.toLowerCase();

//   if (platform.indexOf('mac') !== -1) return '⌘';
//   return 'Ctrl';
// };

// const getControlShift = (key: string) => {
//   const platform = navigator.userAgent.toLowerCase();

//   if (platform.indexOf('mac') !== -1) return `⇧⌘${key}`;
//   return `Ctrl + Shift + ${key}`;
// };

export const FlowMenu: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const translation = useTranslate();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { service_id, workflow_id } = useParams();
  const { admin, permissions } = usePermissions(service_id as string, Resource.WORKFLOW);
  const { refreshModules } = useWorkflow();

  const [fileAnchorEl, setFileAnchorEl] = useState<null | HTMLElement>(null);
  const [viewAnchorEl, setViewAnchorEl] = useState<null | HTMLElement>(null);
  const [viewExport, setViewExport] = useState<null | HTMLElement>(null);

  const menuOpen = Boolean(fileAnchorEl);
  const tab_flow_id = useSelector((state: State) => state.tabs.active.flow_id);
  const data_prefixes = useSelector((state: State) => state.tabs.active.flow?.data_prefixes) || {};
  const service_path = useSelector((state: State) => state.tabs.active.service_path);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);
  const label = useSelector((state: State) => state.tabs.active?.label);

  const saved = useSelector((state: State) => state.tabs.active.saved);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const trigger = commands.find((command) => command?.config?.trigger);
  const tabs = useSelector((state: State) => state.tabs.list);
  const flows = useSelector((state: State) => state.flow.flows);

  const show_data = useSelector((state: State) => state.tabs.active.show_data);
  const show_execution = useSelector((state: State) => state.tabs.active.show_execution);
  const show_tools = useSelector((state: State) => state.tabs.active.show_tools);

  const isUnstoredTab = useSelector((state: State) => state.tabs.active.unstored);
  const isModuleWorkflow = trigger?.config?.command_id === 'workflow.module';
  const multipleUnsaved = useSelector((state: State) => state.tabs.list).filter((tab) => !tab.saved).length > 1;

  const username = useSelector((state: State) => state.auth.username);
  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  const allowUpdate = admin || permissions?.update;
  const allowCreate = admin || permissions?.create;
  const allowDelete = admin || permissions?.delete;

  const { mutateAsync: save, status } = useMutation({
    mutationFn: saveWorkflow,
    onSuccess: (data) => {
      if (!data) return;
      if (data?.error || data?.data?.error) {
        enqueueSnackbar(data?.data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to save workflow!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        dispatch(tabActions.setSavedById({ id: data.flow_id, saved: false }));

        return;
      } else enqueueSnackbar('Workflow saved successfully!', { variant: 'success', autoHideDuration: 3000 });

      if (isModuleWorkflow) {
        try {
          refreshModules({ service_id, workflow_id });
        } catch (e) {}
      }

      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      dispatch(tabActions.setSavedById({ id: data.flow_id, saved: true }));
      queryClient.invalidateQueries({ queryKey: ['flow_info'] });
      setFileAnchorEl(null);
    },
  });

  const { mutateAsync: saveAllTabs, status: saveAllStatus } = useMutation({
    mutationFn: saveWorkflow,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.data?.error) {
        dispatch(tabActions.setSavedById({ id: data.flow_id, saved: false }));
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      dispatch(tabActions.setSavedById({ id: data.flow_id, saved: true }));
      queryClient.invalidateQueries({ queryKey: ['flow_info'] });
      setFileAnchorEl(null);
    },
  });

  const saveFlow = () => {
    if (!commands.some((command) => command?.config?.trigger)) {
      return enqueueSnackbar('Each workflow must include a trigger node.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }

    if (!service_path && !workflow_id) {
      if (trigger) dispatch(tabActions.setSelectedNode(trigger.node.id));

      return enqueueSnackbar(
        'Please define a custom path for this endpoint. You can do this in the trigger nodes settings.',
        {
          variant: 'error',
          autoHideDuration: 12000,
        }
      );
    }

    if (flows.filter((flow) => flow.service_path === service_path && flow.flow_id !== tab_flow_id).length >= 1) {
      return enqueueSnackbar(
        'This flow has the same service path as another flow. Please change the service path by selecting the trigger node and updating the text field at the top.',
        {
          variant: 'error',
          autoHideDuration: 10000,
        }
      );
    }

    enqueueSnackbar('Saving workflow...', { variant: 'info', autoHideDuration: 3000 });

    save({
      config: translation.translate(tab_flow_id),
      username: username || '',
      service_id: service_id as string,
      service_path: (service_path || workflow_id) as string,
      trigger_type: trigger?.config?.command_id as string,
      authentication,
      data_prefixes,
    });
  };

  const saveAsTemplate = () => {
    if (!trigger) {
      return enqueueSnackbar('Template must include a trigger node.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }

    const data = {
      name: label || '',
      data: translation.translate(tab_flow_id),
      type: TriggerCommandIdMap[trigger?.config?.command_id as keyof typeof TriggerCommandIdMap] as TemplateType,
      trigger_type: trigger?.config?.command_id as string,
      data_prefixes: '',
    };

    dispatch(flowActions.setFlowModal({ type: FlowModals.SaveAsTemplate, data }));
    setFileAnchorEl(null);
  };

  const saveAll = async () => {
    let error = false;
    const tabsToBeSaved = tabs.filter((tab) => !tab.saved);

    enqueueSnackbar('Saving multiple workflows...', { variant: 'info', autoHideDuration: 3000 });

    for (const tab of tabsToBeSaved) {
      const { flow_id: id, service_path, commands: tab_commands } = tab;

      if (!tab_commands.some((command) => command?.config?.trigger)) {
        error = true;
        return enqueueSnackbar(`(${service_path}) Each workflow must include a trigger node.`, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }

      if (!service_path && !workflow_id) {
        if (trigger) dispatch(tabActions.setSelectedNode(trigger.node.id));

        error = true;
        return enqueueSnackbar(
          `(${service_path}) Please define a custom path for this endpoint. You can do this in the trigger nodes settings.`,
          {
            variant: 'error',
            autoHideDuration: 12000,
          }
        );
      }

      if (flows.filter((flow) => flow.service_path === service_path && flow.flow_id !== tab.flow_id).length >= 1) {
        return enqueueSnackbar(
          'One or more flows have the same service path. Please change the service path by selecting the trigger node of the offending flow and updating the text field at the top.',
          {
            variant: 'error',
            autoHideDuration: 3000,
          }
        );
      }

      const response = await saveAllTabs({
        config: translation.translate(id),
        username: username || '',
        service_id: service_id as string,
        service_path: service_path as string,
        trigger_type: trigger?.config?.command_id as string,
        authentication,
        data_prefixes,
      });

      if (response?.error) {
        error = true;
      }
    }

    if (!error) {
      enqueueSnackbar('All workflows saved successfully!', { variant: 'success', autoHideDuration: 3000 });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      dispatch(tabActions.setAllSaved());
      setFileAnchorEl(null);
    } else
      enqueueSnackbar('One or more workflows failed to save. All files have NOT been saved.', {
        variant: 'error',
        autoHideDuration: 8000,
      });
  };

  const renameFlow = () => {
    dispatch(flowActions.setFlowModal({ type: FlowModals.EditFlow }));
    setFileAnchorEl(null);
  };

  const duplicateFlow = () => {
    const id = uuidv4();
    dispatch(tabActions.duplicate({ tab_flow_id, id }));
    dispatch(flowActions.setFlowModal({ type: FlowModals.EditFlow }));
    navigate(`/${service_id}/workflow/${id}`);
    setFileAnchorEl(null);
  };

  const deleteFlow = () => {
    if (isUnstoredTab) {
      dispatch(flowActions.setCloseTab(tab_flow_id));
      setFileAnchorEl(null);
      return;
    }

    dispatch(flowActions.setDeleteTab({ id: tab_flow_id, returnPath: 'workflow' }));
    setFileAnchorEl(null);
  };

  const downloadAsJson = () => {
    try {
      const data = translation.translate(tab_flow_id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${service_path || workflow_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setViewExport(null);
    } catch (e) {
      enqueueSnackbar('Failed to download workflow as JSON!', { variant: 'error', autoHideDuration: 5000 });
    }
  };

  const isDisabledSave = Boolean(status === 'pending' || saveAllStatus === 'pending' || saved);

  return (
    <Container>
      <Stack direction='row' alignItems='center' spacing={0.5}>
        <Button tiny onClick={(event) => setFileAnchorEl(event.currentTarget)}>
          File
        </Button>
        <Button tiny onClick={(event) => setViewAnchorEl(event.currentTarget)}>
          View
        </Button>
        {isTelnyxUser && (
          <Button tiny onClick={(event) => setViewExport(event.currentTarget)}>
            Export
          </Button>
        )}
        <Button
          data-intercom-target='canvas-save-workflow-button'
          tiny
          endIcon={
            status === 'pending' || saveAllStatus === 'pending' ? (
              <CircularProgress size={14} sx={{ color: theme.text.subtext }} />
            ) : undefined
          }
          onClick={saveFlow}
          disabled={isDisabledSave || !allowCreate}
        >
          Save
        </Button>
      </Stack>
      {isTelnyxUser && (
        <StyledMenu
          anchorEl={viewExport}
          open={Boolean(viewExport)}
          onClose={() => setViewExport(null)}
          anchorOrigin={{
            vertical: 25,
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem onClick={downloadAsJson} disableRipple>
            <ListItemIcon>
              <CloudDownloadOutlined />
            </ListItemIcon>
            <ListItemText>Download JSON</ListItemText>
          </MenuItem>
        </StyledMenu>
      )}
      <StyledMenu
        anchorEl={viewAnchorEl}
        open={Boolean(viewAnchorEl)}
        onClose={() => setViewAnchorEl(null)}
        anchorOrigin={{
          vertical: 25,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => dispatch(tabActions.updateEdgeVisibility('execution'))} disableRipple>
          <ListItemIcon>
            <ExecutionIcon active={show_execution} />
          </ListItemIcon>
          <ListItemText>Execution</ListItemText>
          <Typography variant='body2' color='text.secondary'>
            {show_execution ? 'hide' : 'show'}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => dispatch(tabActions.updateEdgeVisibility('data'))} disableRipple>
          <ListItemIcon>
            <AttributeIcon color={theme.telnyx.blue} active={show_data} />
          </ListItemIcon>
          <ListItemText>Data</ListItemText>
          <Typography variant='body2' color='text.secondary'>
            {show_data ? 'hide' : 'show'}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => dispatch(tabActions.updateEdgeVisibility('tool'))} disableRipple>
          <ListItemIcon>
            <AttributeIcon color={theme.telnyx.orange} active={show_tools} />
          </ListItemIcon>
          <ListItemText>Tools</ListItemText>
          <Typography variant='body2' color='text.secondary'>
            {show_tools ? 'hide' : 'show'}
          </Typography>
        </MenuItem>
      </StyledMenu>
      <StyledMenu
        anchorEl={fileAnchorEl}
        open={menuOpen}
        onClose={() => setFileAnchorEl(null)}
        anchorOrigin={{
          vertical: 25,
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={saveFlow}
          disabled={saved || status === 'pending' || saveAllStatus === 'pending' || !allowCreate}
        >
          <ListItemIcon>
            {status === 'pending' || saveAllStatus === 'pending' ? (
              <CircularProgress size={12} sx={{ ml: '2px' }} />
            ) : (
              <SaveAlt fontSize='small' />
            )}
          </ListItemIcon>
          <ListItemText>Save</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            {`${getControlIcon()} S`}
          </Typography> */}
        </MenuItem>
        <MenuItem onClick={saveAll} disabled={!multipleUnsaved || !allowCreate}>
          <ListItemIcon>
            {status === 'pending' || saveAllStatus === 'pending' ? (
              <CircularProgress size={12} sx={{ ml: '2px' }} />
            ) : (
              <Save fontSize='small' />
            )}
          </ListItemIcon>
          <ListItemText>Save All</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            {getControlShift('S')}
          </Typography> */}
        </MenuItem>
        <MenuItem onClick={saveAsTemplate} disabled={!allowCreate}>
          <ListItemIcon>
            {status === 'pending' ? <CircularProgress size={12} sx={{ ml: '2px' }} /> : <Save fontSize='small' />}
          </ListItemIcon>
          <ListItemText>Save as template</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            {getControlShift('T')}
          </Typography> */}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={renameFlow} disabled={!allowUpdate}>
          <ListItemIcon>
            <Edit fontSize='small' />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            {`${getControlIcon()} E`}
          </Typography> */}
        </MenuItem>
        <MenuItem onClick={duplicateFlow} disableRipple disabled={!allowCreate}>
          <ListItemIcon>
            <FileCopy fontSize='small' />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            {`${getControlIcon()} D`}
          </Typography> */}
        </MenuItem>
        <MenuItem onClick={deleteFlow} disableRipple disabled={!allowDelete}>
          <ListItemIcon>
            <Delete fontSize='small' />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
          {/* <Typography variant='body2' color='text.secondary'>
            DEL
          </Typography> */}
        </MenuItem>
      </StyledMenu>
    </Container>
  );
};
